import * as jsonpatch from 'fast-json-patch';
import { postRequest, patchRequest, deleteRequest, getRequest } from '../../../core/utils/api';
import { type KeycloakContext } from '../../authentication/keycloak/KeycloakProvider';
import type Facility from '../models/facility';
import { dataCache, FACILITIES_CACHE_KEY } from '../../../core/utils/data-cache';

export const createFacility = async (facility: Facility, keycloak: KeycloakContext): Promise<Facility | undefined> => {
  const apiResponse = await postRequest<Facility>('facility', keycloak, facility);

  if (apiResponse.error || apiResponse.json === undefined) {
    return undefined;
  }

  // Invalidate the cached facilities list so the new entry is fetched on next visit.
  dataCache.invalidate(FACILITIES_CACHE_KEY);
  return apiResponse.json;
};

export const updateFacility = async (
  originalFacility: Facility,
  changedFacility: Facility,
  keycloak: KeycloakContext,
): Promise<Facility | undefined> => {
  if (originalFacility.id === undefined) {
    return undefined;
  }

  const apiResponse = await patchRequest<Facility>(
    `facility/${originalFacility.id}`,
    keycloak,
    jsonpatch.compare(originalFacility, changedFacility),
  );

  if (apiResponse.error) {
    return undefined;
  }

  // Invalidate the cached facilities list to reflect the updated entry.
  dataCache.invalidate(FACILITIES_CACHE_KEY);
  return apiResponse.json;
};

export const deleteFacilityById = async (id: string, keycloak: KeycloakContext): Promise<boolean> => {
  const apiResponse = await deleteRequest<boolean>(`facility/${id}`, keycloak);

  if (apiResponse.error) {
    return false;
  }

  // Invalidate the cached facilities list so the deleted entry is not shown.
  dataCache.invalidate(FACILITIES_CACHE_KEY);
  return true;
};

export const getAllFacilitiesForAdminOrAuthorEmail = async (keycloak: KeycloakContext): Promise<Facility[] | undefined> => {
  const apiResponse = await getRequest<Facility[]>('facility', keycloak);
  if (apiResponse.error) {
    return undefined;
  }
  return apiResponse.json;
};
